import { Box, createStyles, Divider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex } from "@rpi/openapi-core";
import React from "react";
import { CellLink } from "./cells/CellLink";
import { OrderItemStatusCell } from "./cells/OrderItemStatusCell";
import { tokens } from "@blurb/codex-react";
import { Body, Label } from "./Typography";
import { formatCurrencyV2 } from "../utils/misc.util";
import { featureFlags } from "../featureFlags";
import {
  formatItemTitle,
  isAssetPurged,
  getSkuThumbnailUrl,
  groupValidationEntries,
  getVisibleErrors,
  getVisibleAutofixes,
  getVisibleInformatives,
  hasAnyValidationMessages,
  AssetValidation,
  ValidationEntry,
} from "./orderItemCardHelpers";

const useStyles = createStyles({
  root: {
    gap: tokens.spacing["5"],
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  thumbnail: {
    width: 80,
    height: 80,
    borderRadius: tokens.radius.base,
    flexShrink: 0,
    backgroundColor: `${tokens.color.primitive.light.foam["50"]}`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  contentColumn: {
    gap: tokens.spacing["5"],
    flex: 1,
    minWidth: 0,
  },
  detailsRow: {
    gap: tokens.spacing["4"],
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  detailColumn: {
    gap: tokens.spacing["1"],
    flex: "1 1 280px",
    minWidth: 280,
  },
  assetsCard: {
    border: `1px solid ${tokens.color.semantic.bg.surfaceSunken}`,
    borderRadius: tokens.radius.base,
    padding: tokens.spacing["4"],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing["4"],
    flex: "1 1 280px",
    minWidth: 280,
    maxHeight: 400,
    overflowY: "auto" as const,
  },
  filesColumn: {
    gap: tokens.spacing["2"],
  },
  filesHeader: {
    gap: tokens.spacing["1"],
    alignItems: "center",
  },
  filesTitle: {
    color: tokens.color.semantic.text.subtle,
  },
  fileRow: {
    gap: tokens.spacing["1"],
    alignItems: "center",
  },
  detailRow: {
    gap: tokens.spacing["1"],
    alignItems: "center",
  },
  detailLabel: {
    flexShrink: 0,
    whiteSpace: "nowrap" as const,
  },
  detailValue: {
    flex: 1,
    minWidth: 0,
    overflowWrap: "anywhere" as const,
  },
  validationList: {
    margin: 0,
    paddingLeft: tokens.spacing["6"],
    listStyleType: "disc",
  },
  errorItem: {
    color: tokens.color.semantic.text.danger,
  },
  autofixItem: {
    color: tokens.color.semantic.text.subtle,
  },
  informativeItem: {
    color: tokens.color.semantic.text.subtle,
  },
  fileRowWrapper: {
    gap: tokens.spacing["1"],
  },
  validationWrapper: {
    gap: tokens.spacing["1"],
  },
});

export type OrderItemFromQuery = GraphQL.OrderItemFieldsFragment;

interface OrderItemCardProps {
  orderItem: OrderItemFromQuery;
  currency: GraphQL.SupportedCurrency;
  isSuperAdmin: boolean;
}

export const OrderItemCard: React.FC<OrderItemCardProps> = ({ orderItem, currency, isSuperAdmin }) => {
  const { classes } = useStyles();

  const title = formatItemTitle(orderItem.id, orderItem.customerProduct?.name);
  const thumbnailUrl = getSkuThumbnailUrl(orderItem.openapiProduct?.openapiSku) ?? orderItem.imageUri;
  const amount = formatLineItemAmount(currency, orderItem.unitPrice, orderItem.quantity);

  const isPurged = isAssetPurged(orderItem.printAsset?.status);
  const coverUrl = isPurged ? null : orderItem.printAsset?.preflightOutputFiles?.cover?.srcUrl;
  const gutsUrl = isPurged ? null : orderItem.printAsset?.preflightOutputFiles?.guts?.srcUrl;
  const validation = groupValidationEntries(
    featureFlags.assetServiceValidation ? orderItem.assetServiceValidationResult : orderItem.validationResult
  );

  const hasFiles = !!(coverUrl || gutsUrl || isPurged);
  const hasStatus = !!orderItem.orderItemStatus;
  const hasValidation = hasAnyValidationMessages(validation, isSuperAdmin);
  const showFilesSection = hasFiles || hasValidation;
  const showAssetsCard = showFilesSection || hasStatus;

  return (
    <Flex.Row className={classes.root}>
      <Box className={classes.thumbnail} sx={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined} />

      <Flex.Column className={classes.contentColumn}>
        <Body size="lg" weight="bold">
          {title}
        </Body>
        <Flex.Row className={classes.detailsRow}>
          <ItemDetails orderItem={orderItem} amount={amount} />
          {showAssetsCard && (
            <ItemAssets
              showFilesSection={showFilesSection}
              hasStatus={hasStatus}
              isPurged={isPurged}
              coverUrl={coverUrl}
              gutsUrl={gutsUrl}
              status={orderItem.orderItemStatus}
              validation={validation}
              isSuperAdmin={isSuperAdmin}
            />
          )}
        </Flex.Row>
      </Flex.Column>
    </Flex.Row>
  );
};

const ItemDetails: React.FC<{ orderItem: OrderItemFromQuery; amount: string }> = ({ orderItem, amount }) => {
  const { classes } = useStyles();
  return (
    <Flex.Column className={classes.detailColumn}>
      {orderItem.openapiProduct?.openapiSku && (
        <DetailRow label="Product SKU" value={orderItem.openapiProduct.openapiSku} />
      )}
      {orderItem.customerProduct?.description && (
        <DetailRow label="Description" value={orderItem.customerProduct.description} />
      )}
      {orderItem.pagecount != null && <DetailRow label="Pages (Per Qty)" value={String(orderItem.pagecount)} />}
      {orderItem.quantity != null && <DetailRow label="Qty" value={orderItem.quantity.toLocaleString()} />}
      <DetailRow label="Amount" value={amount} />
    </Flex.Column>
  );
};

interface ItemAssetsProps {
  showFilesSection: boolean;
  hasStatus: boolean;
  isPurged: boolean;
  coverUrl?: string | null;
  gutsUrl?: string | null;
  status?: GraphQL.OrderItemStatus | null;
  validation: ReturnType<typeof groupValidationEntries>;
  isSuperAdmin: boolean;
}

const ItemAssets: React.FC<ItemAssetsProps> = ({
  showFilesSection,
  hasStatus,
  isPurged,
  coverUrl,
  gutsUrl,
  status,
  validation,
  isSuperAdmin,
}) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.assetsCard}>
      <Flex.Row className={classes.filesHeader}>
        {showFilesSection && (
          <Body weight="bold" className={classes.filesTitle}>
            Files
          </Body>
        )}
        {hasStatus && <OrderItemStatusCell status={status} />}
      </Flex.Row>
      {showFilesSection && hasStatus && <Divider sx={{ borderColor: tokens.color.semantic.bg.surfaceSunken }} />}
      {showFilesSection && (
        <Flex.Column className={classes.filesColumn}>
          <FileRow
            label="Cover"
            testId="order-item-file-row-cover"
            file={{ url: coverUrl, isPurged }}
            validation={validation.cover}
            isSuperAdmin={isSuperAdmin}
          />
          <FileRow
            label="Pages"
            testId="order-item-file-row-pages"
            file={{ url: gutsUrl, isPurged }}
            validation={validation.pages}
            isSuperAdmin={isSuperAdmin}
          />
          <FileRow
            label="Dust jacket"
            testId="order-item-file-row-dust-jacket"
            validation={validation.dustJacket}
            isSuperAdmin={isSuperAdmin}
          />
        </Flex.Column>
      )}
    </Box>
  );
};

interface FileRowProps {
  label: string;
  testId: string;
  /** Omitted by rows that carry verdicts only and have no file to link. */
  file?: { url?: string | null; isPurged: boolean };
  validation: AssetValidation;
  isSuperAdmin: boolean;
}

const FileRow: React.FC<FileRowProps> = ({ label, testId, file, validation, isSuperAdmin }) => {
  const { classes } = useStyles();
  const errors = getVisibleErrors(validation, isSuperAdmin);
  const autofixes = getVisibleAutofixes(validation, isSuperAdmin);
  const informatives = getVisibleInformatives(validation, isSuperAdmin);
  const hasValidationMessages = errors.length > 0 || autofixes.length > 0 || informatives.length > 0;

  const fileLink =
    file && (file.url || file.isPurged) ? <FileLink url={file.url} isPurged={file.isPurged} label={label} /> : null;

  if (!fileLink && !hasValidationMessages) return null;

  return (
    <Flex.Column className={classes.fileRowWrapper} data-testid={testId}>
      <Flex.Row className={classes.fileRow}>
        <Label>{label}:</Label>
        {fileLink ?? (file && <Body>-</Body>)}
      </Flex.Row>
      {hasValidationMessages && (
        <ValidationMessages errors={errors} autofixes={autofixes} informatives={informatives} />
      )}
    </Flex.Column>
  );
};

interface FileLinkProps {
  url?: string | null;
  isPurged: boolean;
  label: string;
}

const FileLink: React.FC<FileLinkProps> = ({ url, isPurged, label }) => {
  const displayName = `${label} file`;

  if (url) {
    return (
      <CellLink
        to={url}
        external
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: tokens.color.semantic.text.bold,
          fontWeight: 600,
          textDecoration: "underline",
          "&:hover": { color: tokens.color.semantic.text.bold },
        }}
      >
        {displayName}
      </CellLink>
    );
  }

  if (isPurged) {
    return <Body>{displayName} (expired)</Body>;
  }

  return null;
};

interface ValidationMessagesProps {
  errors: ValidationEntry[];
  autofixes: ValidationEntry[];
  informatives: ValidationEntry[];
}

const ValidationMessages: React.FC<ValidationMessagesProps> = ({ errors, autofixes, informatives }) => {
  const { classes } = useStyles();
  return (
    <Flex.Column className={classes.validationWrapper}>
      {errors.length > 0 && (
        <ul className={classes.validationList}>
          {errors.map((entry, index) => (
            <li key={`error-${index}`} className={classes.errorItem}>
              <Body size="base" className={classes.errorItem}>
                Error - {entry.humanReadableString}
              </Body>
            </li>
          ))}
        </ul>
      )}
      {autofixes.length > 0 && (
        <ul className={classes.validationList}>
          {autofixes.map((entry, index) => (
            <li key={`autofix-${index}`} className={classes.autofixItem}>
              <Body size="base" className={classes.autofixItem}>
                AutoFix - {entry.humanReadableString}
              </Body>
            </li>
          ))}
        </ul>
      )}
      {informatives.length > 0 && (
        <ul className={classes.validationList}>
          {informatives.map((entry, index) => (
            <li key={`informative-${index}`} className={classes.informativeItem}>
              <Body size="base" className={classes.informativeItem}>
                Informative - {entry.humanReadableString}
              </Body>
            </li>
          ))}
        </ul>
      )}
    </Flex.Column>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { classes } = useStyles();
  return (
    <Flex.Row className={classes.detailRow}>
      <Label className={classes.detailLabel}>{label}:</Label>
      <Body className={classes.detailValue}>{value}</Body>
    </Flex.Row>
  );
};

function formatLineItemAmount(
  currency: GraphQL.SupportedCurrency,
  unitPrice?: number | null,
  quantity?: number | null
): string {
  if (unitPrice === null || unitPrice === undefined || quantity === null || quantity === undefined) return "-";
  const total = unitPrice * quantity;
  return formatCurrencyV2(currency, total);
}
